/**
 * Where a puck lives, and what the page behind calls the place it lives in.
 *
 * The focus view puts a `Dewar › Rack › Puck` line over every puck it draws.
 * That line is only worth printing if it says the SAME words the Manage
 * Products page says: a breadcrumb that reads "Dewar 3" while the screen behind
 * reads "HD-CRYO-2" is worse than no breadcrumb at all, because the user cannot
 * tell whether the voice command is pointed at the thing they are looking at.
 *
 * So the walk and the headings are both copied from the page, not invented:
 *
 *   walk      `ManageProducts.startAutoNavigate` (:144-228) —
 *             `dewarData[lab].data[dewar].racks[i].pucks[j]`, serial compared
 *             trimmed and upper-cased. The id branch is `findStoredPuck`'s
 *             (`VoiceCommandModal.vue:1230-1251`): `String(puck._id)`.
 *   dewar     `DewarSection.vue:502-506` renders
 *             `!dewar.customId ? "Dewar " + (index + 1) : dewar.customId`.
 *   rack      `RackSection.vue:61-63` renders `idsInput[index]`, which is
 *             `this.dewar.map((rack) => rack?.customId)` (`:1607-1609`).
 *
 * Nothing here reads the store, so a test can hand it a plain fixture and a
 * caller can hand it whatever the store happens to hold at that moment.
 */

/** Normalized serial: the exact form both call sites compare on. */
function serialKey(value) {
  return String(value == null ? "" : value)
    .trim()
    .toUpperCase();
}

/**
 * Find one puck by serial and/or id.
 *
 * Either identifier alone is enough and they are OR-ed, exactly as
 * `findStoredPuck` does: the relay resolves a puck to an id when it can and to
 * a spoken serial when it cannot, and a caller holding both should not have to
 * guess which one the store will agree with.
 *
 * @param {Array} dewarData        `$store.state.dewarData` — labs, each `.data`
 *                                 a list of dewars, each `.racks` a list of
 *                                 racks, each `.pucks` a list of pucks.
 * @param {Array} unassignedPucks  `$store.state.unassignedPucks`.
 * @param {{serial?: string, id?: string}} want
 * @returns {{puck: object, rack: object|null, rackIndex: number|null,
 *            dewar: object|null, dewarIndex: number|null, lab: object|null}|null}
 *          `rack` / `rackIndex` / `dewar` / `dewarIndex` are null for a puck
 *          that is in the unassigned pool rather than in a rack.
 */
export function locatePuck(dewarData, unassignedPucks, want) {
  const wantedSerial = serialKey(want && want.serial);
  const wantedId = want && want.id != null ? String(want.id) : null;
  if (!wantedSerial && !wantedId) return null;

  const matches = (puck) =>
    (!!wantedId && String((puck && puck._id) || "") === wantedId) ||
    (!!wantedSerial && serialKey(puck && puck.serialNo) === wantedSerial);

  for (const lab of dewarData || []) {
    for (const [dewarIndex, dewar] of (lab?.data || []).entries()) {
      for (const [rackIndex, rack] of (dewar?.racks || []).entries()) {
        for (const puck of rack?.pucks || []) {
          if (matches(puck)) {
            return { puck, rack, rackIndex, dewar, dewarIndex, lab };
          }
        }
      }
    }
  }

  // The unassigned pool is searched second and on purpose: a puck that is in a
  // rack AND still listed as unassigned is a stale list, and the racked copy is
  // the one the user is looking at.
  for (const puck of unassignedPucks || []) {
    if (matches(puck)) {
      return {
        puck,
        rack: null,
        rackIndex: null,
        dewar: null,
        dewarIndex: null,
        lab: null,
      };
    }
  }

  return null;
}

/**
 * The heading DewarSection prints for a dewar (`DewarSection.vue:502-506`).
 *
 * The fallback needs the dewar's index within its lab, which is why
 * `locatePuck` carries `dewarIndex` — the page's fallback is positional and
 * cannot be recomputed from the document alone.
 */
export function dewarHeading(dewar, dewarIndex) {
  const customId = dewar && dewar.customId;
  if (customId) return String(customId);
  return "Dewar " + (Number(dewarIndex || 0) + 1);
}

/**
 * The heading RackSection prints for a rack (`RackSection.vue:61-63`, fed by
 * the `idsInput` computed at `:1607-1609`).
 *
 * The page renders `customId` into an editable input with no fallback, so a
 * rack without one shows an empty field. An empty breadcrumb segment would read
 * as a missing rack rather than as an unnamed one, so this falls back to the
 * serial the same card prints two rows down ("Rack Serial Number",
 * `RackSection.vue:366-368`) and then to the position, matching the shape of
 * DewarSection's own positional fallback.
 */
export function rackHeading(rack, rackIndex) {
  const customId = rack && rack.customId;
  if (customId) return String(customId);
  const serial = rack && rack.serialNo;
  if (serial) return String(serial);
  return "Rack " + (Number(rackIndex || 0) + 1);
}

/**
 * `['<dewar heading>', '<rack heading>', '<puck serialNo>']`.
 *
 * An unassigned puck has no dewar and no rack, so it gets `['Unassigned', null,
 * serial]` — the null is kept rather than dropped so the renderer can tell a
 * missing middle segment from a short list and stop drawing a separator it has
 * nothing to separate.
 *
 * @param {object|null} located result of `locatePuck`
 * @returns {Array<string|null>} `[]` when nothing was located.
 */
export function breadcrumb(located) {
  if (!located || !located.puck) return [];
  const serial = String(located.puck.serialNo || "").trim() || "Puck";
  if (!located.rack || !located.dewar) return ["Unassigned", null, serial];
  return [
    dewarHeading(located.dewar, located.dewarIndex),
    rackHeading(located.rack, located.rackIndex),
    serial,
  ];
}
